import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { i18n } from 'shared/i18n'
import {
    NavLink,
    withRouter,
} from 'react-router-dom';
import Button from '@mui/material/Button'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import Popover from '@mui/material/Popover'
import useMediaQuery from '@mui/material/useMediaQuery'
import { LangSwitch } from './LangSwitch'
import { SideNavigation } from '../SideNavigation'
import {
    NotificationsIcon,
    ConnectedNotificationsBadge,
} from './NotificationsPanel'
import {
    Popup,
} from './Popup'
import cx from 'classnames';
import './Navigation.css'

const logoColor = (hint) => {
    switch (hint) {
        case '?blue':
        case '?green':
        case '?altgreen':
        case '?red':
        case '?orange':
        case '?yellow':
            return hint.substr(1)
        default: return 'yellow'
    }
}

const NavigationIcon = ({ name }) => <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
    <path d={{
        menu: 'M3 6h18v2H3zm0 5h18v2H3zm0 5h18v2H3z',
        close: 'M18.3 5.7 12 12l6.3 6.3-1.4 1.4-6.3-6.3-6.3 6.3-1.4-1.4L9.2 12 2.9 5.7l1.4-1.4 6.3 6.3 6.3-6.3z',
        account: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm0 2c-4.4 0-8 2-8 4.5V21h16v-2.5C20 16 16.4 14 12 14z',
        more: 'M12 5a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm0 9a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm0 9a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
    }[name]} />
</svg>

export const Navigation = withRouter(({ options, adminOptions, adminPathBase, profile, brand, notifications, onSignOut, location, extraMenu }) => {
    const mobile = useMediaQuery('(max-width: 1599px)')
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [accountAnchor, setAccountAnchor] = useState(null)
    const menuId = useId()
    const accountId = useId()
    const headerRef = useRef(null)
    useLayoutEffect(() => {
        const header = headerRef.current
        const container = header.parentElement
        const measure = () => container.style.setProperty('--navigation-height', `${header.getBoundingClientRect().height}px`)
        measure()
        const observer = new ResizeObserver(measure)
        observer.observe(header)
        return () => {
            observer.disconnect()
            container.style.removeProperty('--navigation-height')
        }
    }, [])
    const closeMenus = () => { setDrawerOpen(false); setAccountAnchor(null) }
    useEffect(() => {
        setDrawerOpen(false)
        setAccountAnchor(null)
    }, [location.pathname, mobile])
    const link = (path, name, props = {}) => <NavLink key={path} to={path} className="navigation-link" onClick={closeMenus} {...props}>
        <Trans>{name}</Trans>
    </NavLink>
    const account = <div className="navigation-account">
        <div className="navigation-account-name">{profile?.displayName || profile?.email || profile?.phoneNumber}</div>
        {extraMenu && <div className="navigation-preference" onClick={closeMenus}>{extraMenu}</div>}
        {link('/profile', 'Profile')}
        {link('/settings', 'Settings')}
        <Button className="navigation-sign-out" onClick={() => { closeMenus(); onSignOut?.() }}><Trans>Sign Out</Trans></Button>
    </div>

    return <>
        <header ref={headerRef} className="navigation-header">
            <div className="navigation-toolbar">
                {mobile && <IconButton className={cx('navigation-menu-trigger', { 'has-notifications': notifications?.length })}
                    aria-label={i18n().t('Menu')} aria-expanded={drawerOpen} aria-controls={drawerOpen ? menuId : undefined}
                    aria-haspopup="dialog" onClick={() => setDrawerOpen(true)}>
                    <NavigationIcon name="menu" />
                </IconButton>}
                <div className="navigation-brand">{brand || <NavLink to="/">
                    <img src={`/static/assets/img/logo_${logoColor(location.search)}250.png`} alt="BookingGood Sport" />
                </NavLink>}</div>
                {!mobile && (options.length > 0 || adminOptions?.length > 0) && <nav className="navigation-links" aria-label={i18n().t('Menu')}>
                    {options.map(([path, name]) => link(path, name))}
                    {!!adminOptions?.length && link(adminOptions[0].path, 'Administration', {
                        isActive: () => location.pathname === adminPathBase || location.pathname.startsWith(`${adminPathBase}/`) || adminOptions.some(({ path }) => location.pathname === path),
                    })}
                </nav>}
                <div className="navigation-actions">
                    {!mobile && !!notifications && <div className="navbar-notifications"><NotificationsIcon /></div>}
                    <LangSwitch dropdown />
                    {!mobile && profile && <IconButton className="navigation-account-trigger" aria-label={i18n().t('Menu')}
                        aria-expanded={!!accountAnchor} aria-controls={accountAnchor ? accountId : undefined} aria-haspopup="dialog"
                        onClick={event => setAccountAnchor(event.currentTarget)}>
                        <NavigationIcon name="account" />
                    </IconButton>}
                </div>
            </div>
        </header>
        <Popover open={!!accountAnchor} anchorEl={accountAnchor} onClose={closeMenus}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { id: accountId, role: 'dialog', 'aria-label': i18n().t('Menu'), className: 'navigation-account-popover' } }}>
            {account}
        </Popover>
        <Drawer open={drawerOpen} onClose={closeMenus}
            slotProps={{ paper: { id: menuId, role: 'dialog', 'aria-labelledby': `${menuId}-title`, className: 'navigation-drawer' } }}>
            <div className="navigation-drawer-heading">
                <span id={`${menuId}-title`}><Trans>Menu</Trans></span>
                <IconButton aria-label={i18n().t('Close')} onClick={closeMenus}><NavigationIcon name="close" /></IconButton>
            </div>
            <nav className="navigation-drawer-links" aria-label={i18n().t('Menu')}>
                {options.map(([path, name]) => link(path, name))}
            </nav>
            {!!adminOptions?.length && <SideNavigation options={adminOptions} onNavigate={closeMenus} />}
            {profile && <nav className="navigation-drawer-links" aria-label={i18n().t('Notifications')}>
                <NavLink to="/notifications" className="navigation-link" onClick={closeMenus}>
                    <Trans>Notifications</Trans><ConnectedNotificationsBadge line />
                </NavLink>
            </nav>}
            <div className="navigation-drawer-language">
                <div className="navigation-section-title"><Trans>Language</Trans></div>
                <LangSwitch />
            </div>
            {profile && account}
        </Drawer>
    </>
})

export class AdminSitesSelector extends React.Component {
    render() {
        const { currentId, sites, onSelect } = this.props
        const show = e => {
            e.preventDefault()
            this.popup.show()
        }

        return <div className="admin-site-selector">
            <IconButton aria-label={i18n().t('Clubs')} onClick={show}><NavigationIcon name="more" /></IconButton>
            <Popup ref={rr => this.popup = rr}>
                <ul className="admin-sites-list">
                    {sites.map(site => <li key={site.id} className={cx({
                        "site-selector-item": true,
                        "selected": currentId === site.id,
                    })}>
                        <button style={{ display: 'flex' }} className="link-button" onClick={e => {
                            e.preventDefault()
                            onSelect(site.id)
                        }}>
                            <img className="site-logo-big" src={site.logoURL} alt={`${site.name} logo`} />
                            &nbsp;
                            <span>{site.name}</span>
                        </button>
                    </li>)}
                    <li>
                        <NavLink to="/register_club" onClick={() => this.popup.hide()}>
                            <Trans>Register sport club</Trans>
                        </NavLink>
                    </li>
                </ul>
            </Popup>
        </div>
    }
}
